import { useState } from 'react';
import { formaterDate } from '../lib/format';
import { Infobulle } from './Infobulle';

const JOURS_COURTS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/** Clé « AAAA-MM » d'une date ISO. */
const moisDe = (iso: string) => iso.slice(0, 7);

/**
 * Choix d'une SÉANCE dans le calendrier, et non d'une date quelconque.
 *
 * POURQUOI PAS UN `input type="date"` : il ne sait ni cercler certains
 * jours ni interdire les autres — `min` et `max` bornent un intervalle,
 * pas un ensemble. Or les dates possibles sont exactement celles que le
 * calendrier de l'école porte pour le jour du créneau : une trentaine de
 * vendredis, avec des trous aux vacances et aux auditions. Laisser
 * saisir n'importe quel jour produirait un décompte de séances qui ne
 * correspond à rien — arrêter un cours du vendredi un mardi ne veut
 * rien dire.
 *
 * La grille n'affiche donc qu'un mois à la fois, avec les séances
 * cerclées de bleu ; tout le reste est inerte.
 */
export function ChoixSeance({
  dates,
  valeur,
  onChanger,
}: {
  /** Dates de séance possibles, en ISO, triées. */
  dates: string[];
  valeur: string;
  onChanger: (date: string) => void;
}) {
  const possibles = new Set(dates);
  // On ouvre sur le mois de la valeur retenue, sinon sur le dernier mois
  // travaillé : une fin anticipée se pose plus souvent vers la fin.
  const [mois, setMois] = useState(
    () => moisDe(valeur || dates[dates.length - 1] || new Date().toISOString().slice(0, 10)),
  );

  const moisPossibles = [...new Set(dates.map(moisDe))];
  const rangMois = moisPossibles.indexOf(mois);

  const [an, m] = mois.split('-').map(Number);
  const premier = new Date(Date.UTC(an!, m! - 1, 1));
  // `getUTCDay()` compte le dimanche à 0 ; le calendrier français
  // commence le lundi.
  const decalage = (premier.getUTCDay() + 6) % 7;
  const nbJours = new Date(Date.UTC(an!, m!, 0)).getUTCDate();

  const cases: (string | null)[] = [
    ...Array.from({ length: decalage }, () => null),
    ...Array.from(
      { length: nbJours },
      (_, i) => `${mois}-${String(i + 1).padStart(2, '0')}`,
    ),
  ];

  return (
    <div className="rounded-md border border-encre-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          className="rounded px-2 py-1 text-sm text-encre-500 hover:bg-encre-100 disabled:opacity-30"
          disabled={rangMois <= 0}
          onClick={() => setMois(moisPossibles[rangMois - 1]!)}
          aria-label="Mois précédent"
        >
          ‹
        </button>
        <span className="text-sm font-medium">
          {MOIS[m! - 1]} {an}
        </span>
        <button
          type="button"
          className="rounded px-2 py-1 text-sm text-encre-500 hover:bg-encre-100 disabled:opacity-30"
          disabled={rangMois < 0 || rangMois >= moisPossibles.length - 1}
          onClick={() => setMois(moisPossibles[rangMois + 1]!)}
          aria-label="Mois suivant"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {JOURS_COURTS.map((j, i) => (
          <span key={i} className="text-[10px] uppercase text-encre-400">
            {j}
          </span>
        ))}
        {cases.map((iso, rang) => {
          if (!iso) return <span key={`vide-${rang}`} />;
          const jour = Number(iso.slice(8));
          const seance = possibles.has(iso);
          const retenue = iso === valeur;
          return (
            // L'enveloppe de l'infobulle est la case de la grille, le
            // bouton la remplit.
            <Infobulle key={iso} balise="div" texte={seance ? `Séance du ${formaterDate(iso)}` : null}>
              <button
                type="button"
                // Seule une seance est cliquable. Les autres jours restent
                // affiches — un calendrier troue serait illisible — mais
                // inertes et pales.
                disabled={!seance}
                onClick={() => onChanger(iso)}
                className={`h-7 w-full rounded-full text-xs ${
                  retenue
                    ? 'bg-blue-600 font-medium text-white'
                    : seance
                      ? 'border border-blue-400 text-blue-700 hover:bg-blue-50'
                      : 'text-encre-300'
                }`}
              >
                {jour}
              </button>
            </Infobulle>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-encre-400">
        Seules les dates de cours de ce créneau sont proposées.
      </p>
    </div>
  );
}
